import axios from 'axios';

const API_BASE_URL = 'https://jurismap-backend-293387287065.us-central1.run.app';
//const API_BASE_URL = 'http://localhost:8080'; // Local backend for development

// Store the Auth0 token getter function
let getAccessTokenSilently = null;

// Configure axios with timeout, headers, and credentials (cookies) for session-based endpoints
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes timeout (backend processing is heavy)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // IMPORTANT: include cookies for session continuity across cross-site requests
});

// Add request interceptor to include Auth0 token
apiClient.interceptors.request.use(
  async (config) => {
    // If we have an Auth0 token getter, include the token in the Authorization header
    if (getAccessTokenSilently) {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get Auth0 token:', error);
        // Continue without token - backend will fall back to session-based auth
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - backend took too long to respond');
      throw new Error('Request timeout. The server is taking too long to respond. Please try again.');
    }
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.status, error.response?.data);
      throw new Error('Server error. Please try again later.');
    }
    throw error;
  }
);

// Function to set the Auth0 token getter
// This should be called once from App.js after Auth0 is initialized
export const setAuth0TokenGetter = (tokenGetter) => {
  getAccessTokenSilently = tokenGetter;
};

export const getUmapEmbeddings = async (filters) => {
  try {
    // Ensure all required fields are present with proper defaults
    const requestPayload = {
      advogados: filters.advogados || [],
      recorrentes: filters.recorrentes || [],
      relatores: filters.relatores || [],
      resultados: filters.resultados || [],
      period_start: filters.period_start || null,
      period_end: filters.period_end || null,
      titulo_keyword: filters.titulo_keyword || '',
      ementa_keyword: filters.ementa_keyword || '',
      ementa_search_mode: filters.ementa_search_mode || 'Correspondência exata',
    };

    console.log('Sending API request to /API_plot_umap_embeddings with payload:', requestPayload);
    
    const response = await apiClient.post('/API_plot_umap_embeddings', requestPayload);
    
    console.log('Received response from /API_plot_umap_embeddings:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error fetching UMAP embeddings:', error.message || error);
    throw error;
  }
};

export const getFilters = async () => {
  try {
    console.log('Sending API request to /API_get_filters');
    
    // Send empty object as body as the endpoint expects POST with empty payload
    const response = await apiClient.post('/API_get_filters', {});
    
    console.log('Received response from /API_get_filters:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error fetching filters:', error.message || error);
    throw error;
  }
};

export const chatbotQuery = async (data) => {
  try {
    console.log('Sending API request to /API_chatbot with data:', data);
    
    const response = await apiClient.post('/API_chatbot', data);
    
    console.log('Received response from /API_chatbot:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error with chatbot query:', error.message || error);
    throw error;
  }
};

export const generateDefense = async (data) => {
  try {
    console.log('Sending API request to /API_generate_defense with data:', data);
    
    const response = await apiClient.post('/API_generate_defense', data);
    
    console.log('Received response from /API_generate_defense:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error generating defense:', error.message || error);
    throw error;
  }
};

export const uploadDefenseTemplate = async (file) => {
  try {
    console.log('Sending API request to /API_upload_defense_template with file:', file.name);
    
    const formData = new FormData();
    formData.append('defense_template', file);
    
    const response = await apiClient.post('/API_upload_defense_template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Received response from /API_upload_defense_template:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error uploading defense template:', error.message || error);
    throw error;
  }
};

export const uploadCaseFile = async (file) => {
  try {
    console.log('Sending API request to /API_upload_case with file:', file.name);
    
    const formData = new FormData();
    formData.append('case_file', file);
    
    const response = await apiClient.post('/API_upload_case', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Received response from /API_upload_case:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error uploading case file:', error.message || error);
    throw error;
  }
};

export const uploadCaseDescription = async (description) => {
  try {
    console.log('Sending API request to /API_upload_case_description with description');
    
    const response = await apiClient.post('/API_upload_case_description', {
      case_description: description
    });
    
    console.log('Received response from /API_upload_case_description:', response.status);
    return response.data;
  } catch (error) {
    console.error('Error uploading case description:', error.message || error);
    throw error;
  }
};

export const generateDefenseStream = async (data, onMessage) => {
  try {
    console.log('Sending streaming API request to /API_generate_defense with data:', data);
    
    const response = await fetch(`${API_BASE_URL}/API_generate_defense`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include', // include cookies for session-bound operations
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');
      
      for (const line of lines) {
        if (line.trim() && line.startsWith('data: ')) {
          try {
            const message = JSON.parse(line.slice(6));
            onMessage(message);
          } catch (parseError) {
            console.warn('Failed to parse streaming message:', line);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error with streaming defense generation:', error.message || error);
    throw error;
  }
};

// Fetch uploaded case visualization data (2D embeddings + hover data).
// Returns null if there's no uploaded case in the current session.
export const getUploadedCaseData = async () => {
  try {
    console.log('Sending API request to /API_get_uploaded_case_data');
    const response = await apiClient.get('/API_get_uploaded_case_data');

    // Some server responses may be plain text when nothing is available
    if (!response || response.data == null) {
      return null;
    }
    if (typeof response.data === 'string') {
      const text = response.data.trim().toLowerCase();
      if (text.includes('no uploaded case data')) {
        return null;
      }
      // Attempt to parse if stringified JSON was returned
      try {
        const parsed = JSON.parse(response.data);
        return parsed;
      } catch {
        // Unrecognized string format; treat as no data
        return null;
      }
    }

    return response.data;
  } catch (error) {
    // Handle 404s as expected behavior (no uploaded case data)
    if (error.response?.status === 404) {
      console.debug('No uploaded case data found (expected when no case uploaded)');
      return null;
    }
    // Log other errors as warnings
    console.warn('Error fetching uploaded case data:', error.response?.status || error.message);
    return null;
  }
};

// Clear user-scoped session data on backend when user logs out
export const clearUserSession = async () => {
  try {
    console.log('Sending API request to /API_logout');
    const response = await apiClient.post('/API_logout');
    console.log('Session cleared successfully:', response.data);
    return response.data;
  } catch (error) {
    console.warn('Error clearing user session:', error.message || error);
    // Don't throw error - logout should succeed even if session cleanup fails
    return null;
  }
};
